import type { ReactNode } from 'react';
import { TeamPresenceEntity } from '@weave/schema-gen-ts/dist/schemas/phone/presence/v1/api.pb';
import type { IconName } from '@frontend/icons';

export const DEVICE_STATE = {
  AVAILABLE: 'available',
  DO_NOT_DISTURB: 'doNotDisturb',
  DISCONNECTED: 'disconnected',
  OCCUPIED: 'occupied',
  RINGING: 'ringing',
  OFFLINE: 'offline',
  SOFTPHONE_OFFLINE: 'softphoneOffline',
} as const;

export type DeviceState = (typeof DEVICE_STATE)[keyof typeof DEVICE_STATE];

export type CardProps = {
  id: string;
  nameplate: ReactNode;
  avatar: ReactNode;
  body: ReactNode;
  actions: {
    icon: IconName;
    label: string;
    onClick: () => void;
    disabled?: boolean;
    tooltip?: string;
    callout?: string;
    element?: ReactNode;
    trackingId?: string;
    size?: 'small' | 'medium' | 'large';
  }[];
  meta?: ReactNode;
};

export type SelectableCardProps = {
  avatar: ReactNode;
  nameplate: ReactNode;
  meta?: ReactNode;
  onChange: (value: boolean) => void;
  isSelected?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
};

export type UserCardProps = {
  id: string;
  addedAt: string;
  type: 'user';
  name: string;
  subtitle?: string;
  searchTerm?: string;
  isOnline?: boolean;
  locations: { id: string; name: string }[];
  orgId: string;
  deviceState: DeviceState;
  deviceName: string;
  deviceExtension: string;
  deviceActivity?: {
    state: 'idle' | 'active';
    timestamp?: string;
  };
  capabilities?: {
    canCall?: boolean;
    canMessage?: boolean;
    hasTeamChatAccess?: boolean;
    canRemove?: boolean;
    isSelf?: boolean;
  };
  onMessageClick?: (userId: string, userName: string, orgId: string) => void;
  onCallClick?: (extension: string) => void;
  onRemoveClick?: (userId: string, userName: string) => void;
  onPinClick?: (id: string, name: string) => void;
  isPinned?: boolean;
  showUnpinIcon?: boolean;
};

export type DeviceCardProps = {
  id: string;
  userId?: string;
  addedAt: string;
  type: 'device';
  name: string;
  searchTerm?: string;
  subtitle: string;
  locations: { id: string; name: string }[];
  orgId: string;
  deviceState: DeviceState;
  userName?: string;
  deviceExtension: string;
  deviceActivity?: {
    state: 'idle' | 'active';
    timestamp?: string;
  };
  capabilities?: {
    canCall?: boolean;
    canMessage?: boolean;
    hasTeamChatAccess?: boolean;
    canRemove?: boolean;
    isSelf?: boolean;
  };
  onMessageClick?: (userId: string, userName: string, orgId: string) => void;
  onCallClick?: (deviceExtension: string) => void;
  onRemoveClick?: (id: string, name: string) => void;
  onPinClick?: (id: string, name: string) => void;
  isPinned?: boolean;
  showUnpinIcon?: boolean;
};

export type UserSelectableData = {
  id: string;
  type: 'user';
  name: string;
  email: string;
  locations: string[];
  locationIds: string[];
};

export type DeviceSelectableData = {
  id: string;
  type: 'device';
  name: string;
  subtitle: string;
  locations: string[];
  locationIds: string[];
};

export type UserSelectableCardProps = Omit<UserSelectableData, 'type'> & {
  searchTerm?: string;
  onChange: (value: boolean) => void;
  isSelected?: boolean;
  disabled?: boolean;
};

export type DeviceSelectableCardProps = Omit<DeviceSelectableData, 'type'> & {
  searchTerm?: string;
  onChange: (value: boolean) => void;
  isSelected?: boolean;
  disabled?: boolean;
};

export type CardData = UserCardProps | DeviceCardProps;
export type CardType = CardData['type'];

export type UserState = 'online' | 'offline' | 'disconnected';

export type CardFilters = {
  location: string[];
  deviceState: DeviceState[];
  cardType: CardType[];
  userStatus: UserState[];
  search: string;
  sortBy: string;
};

export type CardDataLayerProps = {
  filters: CardFilters;
  render: (props: {
    filteredList: CardData[];
    locationList: { label: string; value: string }[];
    showBanner?: boolean;
  }) => ReactNode;
};

export type AddCardDataLayerRenderProps = {
  filters: Pick<CardFilters, 'location' | 'search'>;
  filteredList: {
    users: UserSelectableData[];
    devices: DeviceSelectableData[];
    selected: (UserSelectableData | DeviceSelectableData)[];
  };
  locationList: { label: string; value: string }[];
  isLoading: boolean;
};

export type AddCardDataLayerProps = {
  filters: Pick<CardFilters, 'location' | 'search'>;
  render: (props: AddCardDataLayerRenderProps) => ReactNode;
};

export type StoredEntity = TeamPresenceEntity & { isPinned?: boolean };
